import {forwardRef, useEffect, useState} from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import {TableVirtuoso} from 'react-virtuoso'
import * as d3 from 'd3-format'
import PropTypes from 'prop-types'
import {TableSortLabel} from '@mui/material'
import {useTableViewContext} from '../../store/hooks/tableView'
import {useDistributionDimension} from '../../store/hooks/populationDistributionDimension'
import {setSortColumns, sortDataForSpecialColumns} from './SortHelper'

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => <TableContainer component={Paper} {...props} ref={ref} />),
  Table: (props) => <Table {...props} sx={{borderCollapse: 'separate', tableLayout: 'fixed'}} />,
  TableHead,
  TableRow: ({...props}) => <TableRow {...props} />,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
}
VirtuosoTableComponents.Scroller.displayName = 'Scroller'
VirtuosoTableComponents.Table.displayName = 'Table'
VirtuosoTableComponents.TableHead.displayName = 'TableHead'
VirtuosoTableComponents.TableRow.displayName = 'TableRow'
VirtuosoTableComponents.TableBody.displayName = 'TableBody'

export default function ReactVirtualizedTable({
  data,
  columns,
  height,
  width,
  enablePadding,
  columnData,
  selectedGeoBoundary,
  addEmptyColumn = false,
  chartType = 'density',
  isFilterApplied = false
}) {
  const [sortedData, setSortedData] = useState(data)

  const [order, setOrder] = useState('dsc')
  const [orderBy, setOrderBy] = useState('state')
  const {setSelectedLabel} = useTableViewContext()
  const {selectedDimension, distributionLabel, setDistributionLabel} = useDistributionDimension()
  useEffect(() => {
    if (chartType === 'density') {
      setSortColumns(selectedGeoBoundary, columnData, columns, isFilterApplied, getSortedData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (chartType === 'distribution') {
      const initialOrder = distributionLabel?.order || 'desc'
      const initialOrderBy = distributionLabel?.orderBy || 'count'
      const initialLabel = selectedDimension || 'Income Level'
      getSortedData(initialOrder, initialOrderBy, initialLabel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedDimension])

  const getSortedData = (order, orderBy, label, type) => {
    const isSpecialColumn =
      ((label === 'Income Level' || label === 'Age' || label === 'Length Of Residence (Years)') &&
        orderBy === 'binName') ||
      orderBy === 'congressional_district' ||
      type === 'percentage'

    // Create a shallow copy to avoid mutating the original (potentially immutable) array
    const sortedArr = [...data]

    if (isSpecialColumn) {
      sortDataForSpecialColumns(order, orderBy, label, type, sortedArr)
    } else if (order === 'asc') {
      sortedArr.sort((a, b) => {
        if (a[orderBy] < b[orderBy]) {
          return -1
        }
        if (a[orderBy] > b[orderBy]) {
          return 1
        }
        return 0
      })
    } else {
      sortedArr.sort((a, b) => {
        if (a[orderBy] < b[orderBy]) {
          return 1
        }
        if (a[orderBy] > b[orderBy]) {
          return -1
        }
        return 0
      })
    }

    setOrder(order === 'asc' ? 'desc' : 'asc')
    setOrderBy(orderBy)
    if (chartType === 'distribution') {
      setDistributionLabel({orderBy, order, label})
    } else {
      setSelectedLabel({orderBy, order})
    }
    setSortedData(sortedArr)
  }
  const LEFT_ALIGNED_COLUMNS = new Set(['county', 'congressional_district', 'state', 'binName', 'dominant_persona'])
  const getAlignment = (dataKey) => {
    return LEFT_ALIGNED_COLUMNS.has(dataKey) ? 'left' : 'right'
  }

  const fixedHeaderContent = () => {
    let tableColumns = columns
    if (addEmptyColumn) tableColumns = [...columns, {dataKey: 'dummy', label: '', numeric: false}]
    return (
      <TableRow>
        {tableColumns.map((column) => (
          <TableCell
            tabIndex={0}
            key={column.dataKey}
            variant='head'
            order={order}
            onClick={() => getSortedData(order, column.dataKey, column.label, column.type)}
            align={getAlignment(column.dataKey)}
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              paddingTop: enablePadding ? '66px' : '',
              color: (theme) => theme.palette.hpBlack,
              fontWeight: (theme) => theme.typography.fontWeightSemibold,
              cursor: 'pointer'
            }}
          >
            <TableSortLabel active={orderBy === column.dataKey} direction={order === 'asc' ? 'desc' : 'asc'}>
              {column.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    )
  }
  const getCellValue = (column, row) => {
    if (column.type === 'percentage') {
      return `${row[column.dataKey]}%`
    }
    if (typeof row[column.dataKey] === 'string') {
      return row[column.dataKey]
    } else if (typeof row[column.dataKey] === 'number') {
      return d3.format(',')(row[column.dataKey])
    }
  }
  const rowContent = (_index, row) => {
    let tableColumns = columns
    if (addEmptyColumn) tableColumns = [...columns, {dataKey: 'dummy', label: '', numeric: false}]
    return (
      <>
        {tableColumns.map((column) =>
          column.dataKey === 'dummy' ? (
            <TableCell key={column.dataKey} style={{width: '50px'}} />
          ) : (
            <TableCell
              tabIndex={0}
              key={column.dataKey}
              align={getAlignment(column.dataKey)}
              style={{color: (theme) => theme.palette.hpBlack}}
            >
              {getCellValue(column, row)}
            </TableCell>
          )
        )}
      </>
    )
  }

  return (
    <Paper elevation={0} style={{height, width, boxShadow: 0}}>
      <TableVirtuoso
        key={`${columns.length}-${sortedData.length}-${orderBy}-${order}`}
        data={sortedData}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
      />
    </Paper>
  )
}

ReactVirtualizedTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  enablePadding: PropTypes.bool.isRequired,
  addEmptyColumn: PropTypes.bool,
  columnData: PropTypes.object,
  selectedGeoBoundary: PropTypes.string.isRequired,
  chartType: PropTypes.string,
  isFilterApplied: PropTypes.bool
}
